import { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

interface Article {
  slug: string;
  title: string;
  sortOrder: number;
}

interface Section {
  section: string;
  sectionTitle: string;
  sectionOrder: number;
  articles: Article[];
}

export default function HelpIndex() {
  const { getAccessTokenSilently } = useAuth0();
  const [sections, setSections] = useState<Section[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function loadSections() {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_API_ROOT}/help/sections`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!response.ok) {
          throw new Error(`GET /help/sections answered ${response.status}`);
        }
        const data = await response.json();
        if (!cancelled) {
          setSections(data.data);
        }
      } catch {
        if (!cancelled) {
          setFailed(true);
        }
      }
      if (!cancelled) {
        setLoading(false);
      }
    }
    loadSections();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, attempt]);

  function tryAgain() {
    setFailed(false);
    setLoading(true);
    setAttempt((n) => n + 1);
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="help-index">
      <h1>Help Center</h1>
      <p className="text-muted mb-4">
        Find guides and documentation to help you get the most out of
        AdviceCloud.
      </p>
      {failed && (
        <Alert variant="danger" className="d-flex align-items-center gap-3">
          <span>Help sections did not load. Try again later.</span>
          <Button variant="outline-danger" size="sm" onClick={tryAgain}>
            Try again
          </Button>
        </Alert>
      )}
      <Row>
        {sections.map((section) => (
          <Col md={6} lg={4} key={section.section} className="mb-3">
            <Card className="h-100">
              <Card.Body>
                <Card.Title>{section.sectionTitle}</Card.Title>
                <ul className="list-unstyled mb-0">
                  {section.articles.map((article) => (
                    <li key={article.slug} className="mb-1">
                      <Link to={`/help/${section.section}/${article.slug}`}>
                        {article.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
}
